<template>
  <div>
    <!-- The slot in here displays the supplied component repeating, but only those matching the current page number -->
    <div class="flex flex-col">
      <template v-for="pItem in pagedItems" :key="pItem.item.id">
        <div v-if="pItem.pageNumber === currentPageNumber" @pointerdown="selectItem(pItem.item)">
          <slot name="item" :item="pItem.item" :isSelected="isSelected(pItem.item)">
            {{ pItem }}
          </slot>
        </div>
      </template>
    </div>
  </div>

  <!-- Display the page numbers and allow page selection -->
  <div class="m-2 font-bold flex flex-row self-end items-center">
    <p>{{ t('page') }}</p>
    <div v-for="p of pageNumbers" :key="`page-${p}`" class="mx-1">
      <div
        class="bg-[lightgrey] px-2 py-1 w-6 flex items-center justify-center rounded-sm"
        :class="{ 'bg-yellow': currentPageNumber === p }"
      >
        <span class="cursor-pointer" @pointerdown="selectPage(p)">{{ p }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends { id: number }">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const messages = {}

const { t } = useI18n({ messages })
interface PagedItem {
  pageNumber: number
  item: T
}

const props = defineProps<{
  items: T[]
  selected?: T
  itemsPerPage: number
}>()

const emit = defineEmits<{
  (e: 'update:selected', value: T): void
}>()

const isSelected = (item: T) => props.selected && item === props.selected
const currentPageNumber = ref(1)

// Add paging information to the item data supplied in props
const pagedItems = computed(() => {
  let pageNumber = 0
  const pagedItems: PagedItem[] = []
  props.items.forEach((item, index) => {
    pageNumber += index % props.itemsPerPage > 0 ? 0 : 1
    pagedItems.push({
      pageNumber,
      item,
    })
  })
  return pagedItems
})

const pageNumbers = computed(() => {
  const numbers = new Set(pagedItems.value.map((item) => item.pageNumber))
  return Array.from(numbers)
})

function selectItem(item: T) {
  emit('update:selected', item)
}

function selectPage(n: number) {
  currentPageNumber.value = n
}
</script>

<style scoped></style>
